import { useEffect, useRef, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Skeleton,
  Space,
  Switch,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  DROPBOX_LINK_REGEX,
  EVENT_NAME_REGEX,
  GOOGLE_DRIVE_LINK_REGEX,
} from "../../../../config/constant";
import {
  GetDropBoxAuthUrl,
  GetDropBoxDriveFolders,
  GetDropBoxForceAuthUrl,
  GetEventDetailService,
  GetGoogleDriveFolders,
  ImportFromDriveService,
  ImportFromDropboxService,
  RefreshDropBoxFolder,
  RefreshGoogleDriveFolder,
  ShareEventPicturesToggle,
  UpdateEventPrivacyStatus,
  UpdateEventService,
  UpdateGalleryBtnStatusService,
} from "../../../../apis/events";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { DatePickerInput, Label } from "../../../../components/ui";
import { tostE, tostS } from "../../../../config/toastConfig";
import { EventStatusRender } from "../../../../components/common/StatusRender";
import { Table } from "../../../../components/ui";
import { QRCodeCanvas } from "qrcode.react";
import WhatsappNumberSection from "./Overview/WhatsappNumberSection";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import {
  setDropboxAuthSuccess,
  setDropboxEventId,
} from "../../../../redux/actions/dropboxActions";
import { EventAnalyticsCard } from "./Overview/EventAnalytics";
import { OrganizerDetailsCard } from "./Overview/OrganizerDetailsCard";

export default function EventOverview({ setTitle }) {
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [importDrive, setImportDrive] = useState(false);
  const [assignedOrganizer, setAssignedOrganizer] = useState([]);
  const [copySuccess, setCopySuccess] = useState("");
  const [eventData, setEventData] = useState({});
  const [privateEvent, setPrivateEvent] = useState(false);
  const [privateEventLoading, setPrivateEventLoading] = useState(false);
  const [importDropbox, setImportDropbox] = useState(false);
  const [selectDropBoxAccModal, setSelectDropBoxAccModal] = useState(false);
  const [linkedDropboxDetails, setLinkedDropboxDetails] = useState(null);
  const [sharePictureLoading, setSharePictureLoading] = useState(false);
  const [dropboxLoading, setDropboxLoading] = useState(false);
  const [gDriveFileList, setGDriveFileList] = useState([]);
  const [dropboxFileList, setDropBoxFileList] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [webFlowEnabled, setWebFlowEnabled] = useState(true);
  const [whatsappFlowEnabled, setWhatsappFlowEnabled] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);

  const isAutoLogin = sessionStorage.getItem('from_auto_login') === '1';

  const { user } = useSelector((state) => state.auth);
  const dropboxAuthSuccess = useSelector(
    (state) => state.dropbox.dropboxAuthSuccess,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (dropboxAuthSuccess) {
      setImportDropbox(true);
    }
  }, [dropboxAuthSuccess]);

  const getDropBoxAuthUrl = async () => {
    setDropboxLoading(true);
    if (user?.dropbox_account_id) {
      getDropBoxFileList();
      setSelectDropBoxAccModal(true);
    }
    let body = {
      event_id: id,
    };

    GetDropBoxAuthUrl(body).then((resp) => {
      if (resp.status === 200) {
        dispatch(setDropboxEventId(id));
        if (resp?.data?.url)
          window.open(resp.data.url, "_blank", "noopener,noreferrer");
        else if (
          resp?.data?.success &&
          resp?.data?.dropbox_details?.dropbox_account_id
        ) {
          setLinkedDropboxDetails({ ...resp.data.dropbox_details });
          getDropBoxFileList();
          setSelectDropBoxAccModal(true);
        } else {
          tostE("Something went wrong");
        }
        setDropboxLoading(false);
      }
    });
  };

  const dropboxForceAuth = () => {
    let body = {
      event_id: id,
    };

    GetDropBoxForceAuthUrl(body).then((resp) => {
      if (resp.status === 200) {
        dispatch(setDropboxEventId(id));
        if (resp?.data?.url)
          window.open(resp.data.url, "_blank", "noopener,noreferrer");
        else {
          setSelectDropBoxAccModal(false);
          setImportDropbox(true);
        }
      }
    });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(data)
      .then(() => {
        setCopySuccess("URL copied to clipboard!");
        setTimeout(() => setCopySuccess(""), 2000);
      })
      .catch(() => setCopySuccess("Failed to copy URL!"));
  };

  const getEventDetails = async () => {
    setLoading(true);
    try {
      const res = await GetEventDetailService({ id });
      if (res.status === 200 && res.data.success) {
        setEventData(res.data.event);
        const {
          event_name,
          event_title,
          hastag,
          start_date,
          end_date,
          gallery_btn,
          logo,
          watermark_position,
          max_photo_allowed,
          evnt_status,
          share_button_status_on_whatsapp,
          terms_and_privacy_agreement,
          first_message,
          is_enable_web_flow,
          is_enable_whatsapp_flow,
          show_event_name,
          show_logo,
        } = res.data.event;
        setWebFlowEnabled(!!is_enable_web_flow);
        setWhatsappFlowEnabled(!!is_enable_whatsapp_flow);
        setValues({
          ...values,
          event_name,
          event_title,
          hastag,
          start_date: dayjs(start_date),
          end_date: dayjs(end_date),
          gallery_btn,
          watermark_position,
          max_photo_allowed: max_photo_allowed,
          share_button_status_on_whatsapp: share_button_status_on_whatsapp,
          terms_and_privacy_agreement: terms_and_privacy_agreement,
          first_message: first_message,
          show_event_name: !!show_event_name,
          show_logo: !!show_logo,
        });
        setLogoPreview(logo || "");
        setTitle(event_name);
        setAssignedOrganizer(res.data.organizer_list);
        setPrivateEvent(evnt_status === 1);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventDetails();
  }, []); // eslint-disable-line

  const CreateEventSchema = Yup.object().shape({
    event_name: Yup.string()
      .required("Event name is required")
      .matches(EVENT_NAME_REGEX, "Event name is invalid"),
    event_title: Yup.string().required("Event title is required"),
    hastag: Yup.string().required("Hashtag is required"),
    start_date: Yup.date().required("Start date is required"),
    end_date: Yup.date().required("End date is required"),
    first_message: Yup.string()
      .required("First message is required")
      .test(
        "single-hashtag",
        "The message must contain exactly one hashtag.",
        (value) => {
          const hashtagCount = (value.match(/#/g) || []).length;
          return hashtagCount === 1;
        },
      ),
  });

  const formik = useFormik({
    initialValues: {
      event_name: "",
      event_title: "",
      hastag: "",
      start_date: "",
      end_date: "",
      gallery_btn: true,
      watermark_position: "",
      terms_and_privacy_agreement: "",
      first_message: "",
      show_event_name: false,
      show_logo: false,
    },
    validationSchema: CreateEventSchema,
    onSubmit: async (values, { resetForm, setSubmitting, setErrors }) => {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("event_title", values.event_title);
      formData.append("start_date", values.start_date.format("YYYY-MM-DD"));
      formData.append("end_date", values.end_date.format("YYYY-MM-DD"));
      formData.append("gallery_btn", values.gallery_btn ? 1 : 0);
      if (logoFile) {
        formData.append("logo", logoFile, "logo.png");
      }
      formData.append("watermark_position", values.watermark_position);
      formData.append(
        "terms_and_privacy_agreement",
        values.terms_and_privacy_agreement,
      );
      formData.append("first_message", values.first_message);
      formData.append("show_event_name", values.show_event_name ? 1 : 0);
      formData.append("show_logo", values.show_logo ? 1 : 0);

      try {
        let res = await UpdateEventService(formData);
        if (res.status === 200 && res.data.success) {
          tostS(res.data.message);
          setIsEditing(false);
          setLogoFile(null);
          resetForm();
          getEventDetails();
        } else {
          if (res.data.message) {
            const fieldErrors = {};
            Object.keys(res.data.message).forEach((key) => {
              fieldErrors[key] = res.data.message[key][0];
            });
            setErrors(fieldErrors);
          }
          tostE("Please fix the errors in the form.");
        }
      } catch (error) {
        tostE(error.response.data.message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    setValues,
    errors,
    touched,
    setFieldValue,
    getFieldProps,
    handleSubmit,
    isSubmitting,
  } = formik;

  const handleLogoSelect = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be 5MB or smaller!");
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result?.toString() || "");
      setCrop(undefined);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    return false; // prevent antd's default upload
  };

  const onCropImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, width / height, width, height),
      width,
      height,
    );
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  };

  const applyCrop = () => {
    const image = imgRef.current;
    if (!image || !completedCrop?.width || !completedCrop?.height) {
      setCropModalOpen(false);
      return;
    }
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(completedCrop.width * scaleX);
    canvas.height = Math.round(completedCrop.height * scaleY);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    canvas.toBlob((blob) => {
      if (!blob) return;
      setLogoFile(blob);
      setLogoPreview(URL.createObjectURL(blob));
      setCropModalOpen(false);
    }, "image/png");
  };

  const handleChangeGalleryVisibility = async (checked) => {
    const body = {
      event_id: id,
      status: checked ? 1 : 0,
    };
    setVisibilityLoading(true);
    try {
      const response = await UpdateGalleryBtnStatusService(body);
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        getEventDetails();
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleChangeShareEventPictures = async (checked) => {
    const body = {
      event_id: id,
      status: checked ? 1 : 0,
    };
    setSharePictureLoading(true);
    try {
      const response = await ShareEventPicturesToggle(body);
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        getEventDetails();
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSharePictureLoading(false);
    }
  };

  const getGDriveFileList = async (folderId = "") => {
    setImportLoading(true);
    const body = {
      event_id: id,
      folder_id: folderId,
    };
    try {
      const res = await GetGoogleDriveFolders(body);

      if (res?.status === 200 && res?.data?.success) {
        if (!folderId) {
          setGDriveFileList(res?.data?.data);
        } else {
          setGDriveFileList((prevList) => {
            return [
              ...res?.data?.data,
              ...prevList.filter((item) => item.id !== folderId),
            ];
          });
          tostS(res.data.message);
        }
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setImportLoading(false);
    }
  };

  const getDropBoxFileList = async (folderId = "") => {
    setImportLoading(true);
    const body = {
      event_id: id,
      folder_id: folderId,
    };
    try {
      const res = await GetDropBoxDriveFolders(body);
      if (res?.status === 200 && res?.data.success) {
        if (!folderId) {
          setDropBoxFileList(res?.data?.data);
        } else {
          tostS(res.data.message);
          setDropBoxFileList((prevList) => {
            return [
              ...res?.data?.data,
              ...prevList.filter((item) => item.id !== folderId),
            ];
          });
        }
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setImportLoading(false);
    }
  };

  const getDropBoxFolderRefresh = async (url) => {
    setImportLoading(true);
    const body = {
      event_id: id,
      url: url,
    };
    try {
      const response = await RefreshDropBoxFolder(body);
      if (response.status === 200 && response.data.success) {
        if (response.data.total) {
          getDropBoxFileList();
        }
        tostS(response.data.message);
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setImportLoading(false);
    }
  };

  const getGDriveFolderRefresh = async (url) => {
    setImportLoading(true);
    const body = {
      event_id: id,
      url: url,
    };
    try {
      const response = await RefreshGoogleDriveFolder(body);
      if (response.status === 200 && response.data.success) {
        if (response.data.total) {
          getGDriveFileList();
        }
        tostS(response.data.message);
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setImportLoading(false);
    }
  };

  const handleChangePrivateStatus = async (checked) => {
    const body = {
      event_id: id,
      status: checked ? 1 : 0,
    };
    setPrivateEventLoading(true);
    try {
      const response = await UpdateEventPrivacyStatus(body);
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        getEventDetails();
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPrivateEventLoading(false);
    }
  };

  const DriveImportSchema = Yup.object().shape({
    drive_link: Yup.string()
      .required("Google Drive Link is required")
      .matches(GOOGLE_DRIVE_LINK_REGEX, "Google Drive Link is invalid"),
  });

  const driveFormik = useFormik({
    initialValues: {
      drive_link: "",
    },
    validationSchema: DriveImportSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      let body = {
        event_id: id,
        url: values.drive_link,
      };
      try {
        let res = await ImportFromDriveService(body);
        if (res.status === 200 && res.data.success) {
          tostS(`${res.data.message}`);
          setImportDrive(false);
          getEventDetails();
        }
      } catch (error) {
        tostE(error.response.data.message);
      } finally {
        setSubmitting(false);
        resetForm();
        setImportDrive(false);
      }
    },
  });

  const DropboxImportSchema = Yup.object().shape({
    dropbox_link: Yup.string()
      .required("Dropbox Link is required")
      .matches(DROPBOX_LINK_REGEX, "Dropbox Link is invalid"),
  });

  const dropboxFormik = useFormik({
    initialValues: {
      dropbox_link: "",
    },
    validationSchema: DropboxImportSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      let body = {
        event_id: id,
        url: values.dropbox_link,
      };
      try {
        let res = await ImportFromDropboxService(body);
        if (res.status === 200 && res.data?.success) {
          if (res?.data?.message) tostS(`${res.data.message}`);
          setImportDrive(false);
          getEventDetails();
        } else {
          tostE(res.data.message);
        }
      } catch (error) {
        tostE(error.response.data.message);
      } finally {
        setSubmitting(false);
        resetForm();
        setImportDrive(false);
      }
    },
  });

  const dropboxListColumns = [
    {
      title: "Folder Name",
      dataIndex: "folder_name",
      key: "folder_name",
    },
    {
      title: "Processed Photos",
      dataIndex: "processed_images",
      key: "processed_images",
      render: (processed_images, record) => (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{processed_images}</span>
          <Button
            type="link"
            icon={<i className="fa fa-refresh"></i>}
            onClick={() => handleRefresh(record)}
          />
        </div>
      ),
    },
    {
      title: "Total Photos",
      dataIndex: "total_photos",
      key: "total_photos",
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (url) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          Click here
        </a>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            style={{ backgroundColor: "#dfe0e1" }}
            type="rounded"
            onClick={() => handleResync(record)}
          >
            Resync
          </Button>
        </Space>
      ),
    },
  ];

  const handleResync = (record) => {
    if (record?.url?.includes("dropbox")) {
      getDropBoxFolderRefresh(record?.url);
    } else {
      getGDriveFolderRefresh(record?.url);
    }
  };

  const handleRefresh = (record) => {
    if (record?.url?.includes("dropbox")) {
      getDropBoxFileList(record?.id);
    } else {
      getGDriveFileList(record?.id);
    }
  };

  const handleDownloadQrCode = (canvasId = "event-qr-code", suffix = "QR") => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const canvasWithPadding = document.createElement("canvas");
    const context = canvasWithPadding.getContext("2d");

    const padding = 20;
    canvasWithPadding.width = canvas.width + padding * 2;
    canvasWithPadding.height = canvas.height + padding * 2;

    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvasWithPadding.width, canvasWithPadding.height);
    context.drawImage(canvas, padding, padding);

    const pngUrl = canvasWithPadding
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    let downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `${eventData.event_name}_${suffix}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="card custom-card">
        {loading ? (
          <Skeleton loading={true} active avatar />
        ) : (
          <>
            <div className="event-info d-flex">
              <div className="event-details-section">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="event-title">{eventData.event_title}</h4>
                  {eventData.status && (
                    <EventStatusRender value={eventData.status} />
                  )}
                </div>
                <p className="hashtag">{eventData.hastag}</p>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <Switch
                      size="small"
                      loading={visibilityLoading}
                      checked={values.gallery_btn}
                      onChange={(checked) => {
                        handleChangeGalleryVisibility(checked);
                      }}
                    />
                    <p>Show Event Gallery to everyone</p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Switch
                      size="small"
                      loading={privateEventLoading}
                      checked={privateEvent}
                      onChange={(checked) => {
                        handleChangePrivateStatus(checked);
                      }}
                    />
                    <p>Make this event private</p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Switch
                      size="small"
                      loading={sharePictureLoading}
                      checked={values?.share_button_status_on_whatsapp}
                      onChange={(checked) => {
                        handleChangeShareEventPictures(checked);
                      }}
                    />
                    <p>Share event pictures</p>
                  </div>
                </div>
                <div className="mt-3 d-flex gap-3 flex-wrap">
                  <Button
                    onClick={() => {
                      setImportDrive(true);
                      getGDriveFileList();
                    }}
                    icon={<i className="fa-light fa-file-import"></i>}
                  >
                    Import From Google Drive
                  </Button>
                  <Button
                    onClick={() => getDropBoxAuthUrl()}
                    loading={dropboxLoading}
                    icon={<i className="fa-light fa-file-import"></i>}
                  >
                    Import From Dropbox
                  </Button>
                  {user?.access_level === 1 && (
                    <Button
                      onClick={() => {
                        setIsEditing(true);
                      }}
                      icon={<i className="fa-light fa-edit"></i>}
                    >
                      Edit Event
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {isAutoLogin && !whatsappFlowEnabled && user?.source !== 'eventapp' && (
              <div className="d-flex align-items-start gap-2 mt-3 p-3 rounded" style={{ background: "#f0f0f0", border: "1px solid #d0d0d0" }}>
                <i className="fa-brands fa-whatsapp mt-1" style={{ fontSize: 18, color: "#131517", flexShrink: 0 }} />
                <p className="mb-0" style={{ fontSize: 13, color: "#131517" }}>
                  For the <strong>WhatsApp version</strong>, please contact your <strong>Sales Representative</strong> or <strong>Event POC</strong> to get this feature enabled for your event.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {!loading && eventData.event_name && (
        <div className="card custom-card mt-3">
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            {/* Web Version */}
            <div className="d-flex flex-column align-items-center" style={{ flex: 1, minWidth: 280 }}>
              <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 10 }}>Web Version</p>
              <div className="position-relative w-100 d-flex flex-column align-items-center">
                <div
                  className="d-flex flex-column align-items-center"
                  style={!webFlowEnabled ? { filter: "blur(5px)", pointerEvents: "none", userSelect: "none" } : {}}
                >
                  <p className="qr-text">
                    Scan QR OR{" "}
                    <span onClick={() => handleDownloadQrCode("web-qr-code", "Web_QR")}>Download</span>
                  </p>
                  <QRCodeCanvas
                    id="web-qr-code"
                    bgColor="#FFF"
                    size={150}
                    style={{ height: "auto", maxWidth: "100%" }}
                    value={eventData.web_short_url || "locked"}
                    viewBox="0 0 256 256"
                  />
                  <span
                    className="url-link mt-2"
                    onClick={() => handleCopy(eventData.web_short_url_copy)}
                  >
                    {copySuccess ? <CheckOutlined /> : <CopyOutlined />} Copy URL
                  </span>
                </div>
                {!webFlowEnabled && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center rounded"
                    style={{ background: "rgba(255,255,255,0.55)" }}
                  >
                    <i className="fa fa-lock" style={{ fontSize: 38, color: "#222" }} />
                    <p className="mb-0 mt-2 fw-600" style={{ fontSize: 13, color: "#222" }}>Feature Locked</p>
                  </div>
                )}
              </div>
            </div>

            <div style={{ width: 1, background: "#f0f0f0", alignSelf: "stretch" }} />

            {/* WhatsApp Version */}
            {eventData.whatsapp_short_url && (
              <div className="d-flex flex-column align-items-center" style={{ flex: 1, minWidth: 280 }}>
                <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 10 }}>WhatsApp Version</p>
                <div className="position-relative w-100 d-flex flex-column align-items-center">
                  <div
                    className="d-flex flex-column align-items-center"
                    style={!whatsappFlowEnabled ? { filter: "blur(5px)", pointerEvents: "none", userSelect: "none" } : {}}
                  >
                    <p className="qr-text">
                      Scan QR OR{" "}
                      <span onClick={() => handleDownloadQrCode("whatsapp-qr-code", "WhatsApp_QR")}>Download</span>
                    </p>
                    <QRCodeCanvas
                      id="whatsapp-qr-code"
                      bgColor="#FFF"
                      size={150}
                      style={{ height: "auto", maxWidth: "100%" }}
                      value={eventData.whatsapp_short_url || "locked"}
                      viewBox="0 0 256 256"
                    />
                    <span
                      className="url-link mt-2"
                      onClick={() => handleCopy(eventData.whatsapp_short_url)}
                    >
                      {copySuccess ? <CheckOutlined /> : <CopyOutlined />} Copy URL
                    </span>
                  </div>
                  {!whatsappFlowEnabled && (
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center rounded"
                      style={{ background: "rgba(255,255,255,0.55)" }}
                    >
                      <i className="fa fa-lock" style={{ fontSize: 38, color: "#222" }} />
                      <p className="mb-0 mt-2 fw-600" style={{ fontSize: 13, color: "#222" }}>Feature Locked</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <EventAnalyticsCard id={id} user={user} />
      {sessionStorage.getItem('from_auto_login') !== '1' && (
        <OrganizerDetailsCard
          getEventDetails={getEventDetails}
          id={id}
          loading={loading}
          assignedOrganizer={assignedOrganizer}
          access_level={user?.access_level}
          setLoading={setLoading}
        />
      )}

      {sessionStorage.getItem('from_auto_login') !== '1' && <WhatsappNumberSection />}

      <Modal
        open={importDrive}
        onCancel={() => {
          setImportDrive(false);
        }}
        onOk={() => {
          driveFormik.handleSubmit();
        }}
        title="Import From Drive"
        okText="Import"
        confirmLoading={driveFormik.isSubmitting}
      >
        <div className="form-group">
          <Input
            className={
              driveFormik.touched?.drive_link && driveFormik.errors?.drive_link
                ? "is-invalid"
                : ""
            }
            {...driveFormik.getFieldProps("drive_link")}
            placeholder="Google Drive Link"
          />
          {driveFormik.touched?.drive_link &&
            driveFormik.errors?.drive_link && (
              <div className="invalid-feedback">
                {driveFormik.errors?.drive_link}
              </div>
            )}
        </div>
        <div className="mt-3">
          <Table
            loading={importLoading}
            data={gDriveFileList}
            columns={dropboxListColumns}
          />
        </div>
      </Modal>

      <Modal
        open={selectDropBoxAccModal}
        onCancel={() => {
          setSelectDropBoxAccModal(false);
        }}
        title="Select Dropbox Account"
        footer={null}
      >
        <div>
          <Space style={{ padding: "10px 0" }}>
            <Button
              type="primary"
              onClick={() => {
                setSelectDropBoxAccModal(false);
                setImportDropbox(true);
              }}
              style={{ padding: "20px 16px", lineHeight: "normal" }}
            >
              <span
                style={{
                  display: "inline-block",
                  textAlign: "center",
                  lineHeight: "1.2",
                }}
              >
                {`Continue with ${
                  linkedDropboxDetails?.dropbox_account_name ||
                  user?.dropbox_account_name ||
                  "Existing Account"
                }`}
                <br />
                {`${
                  linkedDropboxDetails?.dropbox_account_email ||
                  user?.dropbox_account_email ||
                  ""
                }`}
              </span>
            </Button>
            <Button
              onClick={dropboxForceAuth}
              style={{ padding: "20px 16px", lineHeight: "normal" }}
            >
              Login with new account
            </Button>
          </Space>
        </div>
      </Modal>

      <Modal
        open={importDropbox}
        onCancel={() => {
          setImportDropbox(false);
          dispatch(setDropboxAuthSuccess(false));
        }}
        onOk={() => {
          dropboxFormik.handleSubmit();
        }}
        title="Import From Dropbox"
        okText="Import"
        confirmLoading={dropboxFormik.isSubmitting}
      >
        <div className="form-group">
          <Input
            className={
              dropboxFormik.touched?.dropbox_link &&
              dropboxFormik.errors?.dropbox_link
                ? "is-invalid"
                : ""
            }
            {...dropboxFormik.getFieldProps("dropbox_link")}
            placeholder="Dropbox Link"
          />
          {dropboxFormik.touched?.dropbox_link &&
            dropboxFormik.errors?.dropbox_link && (
              <div className="invalid-feedback">
                {dropboxFormik.errors?.dropbox_link}
              </div>
            )}
        </div>
        <div className="mt-3">
          <Table
            loading={importLoading}
            data={dropboxFileList}
            columns={dropboxListColumns}
          />
        </div>
      </Modal>

      <Modal
        open={cropModalOpen}
        title="Crop Logo"
        onCancel={() => setCropModalOpen(false)}
        onOk={applyCrop}
        okText="Apply Crop"
        destroyOnClose
      >
        {imgSrc && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
          >
            <img
              ref={imgRef}
              src={imgSrc}
              alt="Crop source"
              style={{ maxWidth: "100%" }}
              onLoad={onCropImageLoad}
            />
          </ReactCrop>
        )}
      </Modal>

      <Drawer
        closable
        distroyOnClose
        title="Edit Update"
        placement="right"
        open={isEditing}
        onClose={() => setIsEditing(false)}
        footer={
          <Space style={{ padding: "10px 0" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSubmit();
              }}
              loading={isSubmitting}
            >
              Update
            </Button>
            <Button
              onClick={() => {
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
          </Space>
        }
        loading={loading}
      >
        <FormikProvider value={formik}>
          <Form layout="vertical" onFinish={handleSubmit}>
            <Label
              label="Event Title"
              error={touched.event_title && errors.event_title}
              helperText={
                touched.event_title && errors.event_title
                  ? errors.event_title
                  : ""
              }
              required={true}
            >
              <Input
                placeholder="Enter event title"
                {...getFieldProps("event_title")}
              />
            </Label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "-8px 0 16px",
              }}
            >
              <Switch
                size="small"
                checked={values.show_event_name}
                onChange={(checked) => setFieldValue("show_event_name", checked)}
              />
              <span style={{ fontSize: 13 }}>Show event name on web page?</span>
            </div>
            <Label
              label="Event Name"
              error={touched.event_name && errors.event_name}
              helperText={
                touched.event_name && errors.event_name ? errors.event_name : ""
              }
              required={true}
            >
              <Input
                placeholder="Enter event name"
                {...getFieldProps("event_name")}
                disabled
              />
            </Label>
            <Label
              label="Hashtag"
              error={touched.hastag && errors.hastag}
              helperText={touched.hastag && errors.hastag ? errors.hastag : ""}
              required={true}
            >
              <Input
                placeholder="Enter hashtag(#hashtag)"
                {...getFieldProps("hastag")}
                disabled
              />
            </Label>
            <Label
              label="Start Date"
              error={touched.start_date && errors.start_date}
              helperText={
                touched.start_date && errors.start_date ? errors.start_date : ""
              }
              required={true}
            >
              <DatePickerInput
                placeholder="Select Start Date"
                showNow={false}
                value={values.start_date}
                onChange={(date) => {
                  setValues({ ...values, start_date: date });
                }}
                minDate={dayjs(new Date())}
                fullWidth={true}
              />
            </Label>
            <Label
              label="End Date"
              error={touched.end_date && errors.end_date}
              helperText={
                touched.end_date && errors.end_date ? errors.end_date : ""
              }
              required={true}
            >
              <DatePickerInput
                placeholder="Select End Date"
                showNow={false}
                value={values.end_date}
                onChange={(date) => {
                  setValues({ ...values, end_date: date });
                }}
                minDate={dayjs(values.start_date)}
                fullWidth={true}
                disabled={values.start_date ? false : true}
              />
            </Label>
            <Label
              label="Terms and Privacy Agreement"
              error={
                touched.terms_and_privacy_agreement &&
                errors.terms_and_privacy_agreement
              }
              helperText={
                touched.terms_and_privacy_agreement &&
                errors.terms_and_privacy_agreement
                  ? errors.terms_and_privacy_agreement
                  : ""
              }
            >
              <Input.TextArea
                placeholder="Enter Terms and Privacy Agreement"
                {...getFieldProps("terms_and_privacy_agreement")}
                rows={4}
              />
            </Label>
            <Label
              label={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    First Message <span style={{ color: "red" }}>*</span>
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginLeft: "8px",
                    }}
                  >
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        const textarea = document.getElementById(
                          "first_message_textarea",
                        );
                        if (textarea) {
                          const cursorPosition = textarea.selectionStart;
                          const textBeforeCursor = textarea.value.substring(
                            0,
                            cursorPosition,
                          );
                          const textAfterCursor =
                            textarea.value.substring(cursorPosition);
                          const updatedText = `${textBeforeCursor}${
                            eventData.hastag + ""
                          } ${textAfterCursor}`;
                          textarea.value = updatedText;
                          setFieldValue("first_message", updatedText);
                          textarea.focus();
                          textarea.setSelectionRange(
                            cursorPosition + 1,
                            cursorPosition + 1,
                          );
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              }
              error={touched.first_message && errors.first_message}
              helperText={
                touched.first_message && errors.first_message
                  ? errors.first_message
                  : ""
              }
            >
              <Input.TextArea
                id="first_message_textarea"
                placeholder="Enter First Message"
                {...getFieldProps("first_message")}
                rows={4}
              />
            </Label>
            <Label label="Logo">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleLogoSelect}
                >
                  <Button icon={<UploadOutlined />}>
                    {logoPreview ? "Replace Logo" : "Upload Logo"}
                  </Button>
                </Upload>
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Event logo preview"
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #e5e5e5",
                      background: "#fafafa",
                    }}
                  />
                )}
              </div>
            </Label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "-8px 0 16px",
              }}
            >
              <Switch
                size="small"
                checked={values.show_logo}
                onChange={(checked) => setFieldValue("show_logo", checked)}
              />
              <span style={{ fontSize: 13 }}>Show logo on web page?</span>
            </div>
          </Form>
        </FormikProvider>
      </Drawer>
    </>
  );
}
